import React, { useState, useCallback, useEffect } from 'react'
import { connect } from 'react-redux'
import { AppState } from '@chegg-wt/core/state'
import { openModal } from '@chegg-wt/core/state/modals/actions'
import {
  CREATE_PROJECT_MODAL,
  DELETE_PROJECT_MODAL,
  MOVE_PROJECT_MODAL,
  EDIT_PROJECT_MODAL,
} from '@chegg-wt/core/state/modals/constants'
import ClientOnlyPortal from '@chegg-wt/core/components/ClientOnlyPortal'
import { LoadingDots } from '@chegg-wt/core/components/LoadingDots'
import { ProjectTypeKeys } from '@chegg-wt/core/constants/projectTypeKey'
import {
  sortProjects,
  starProject,
  unStarProject,
} from '@chegg-wt/core/state/projects/actions'
import {
  projectsSelector,
  areProjectsLoadingSelector,
  projectFilterSelector,
} from '@chegg-wt/core/state/projects/selectors'
import { MODAL_TITLE_CREATE_CITATION_LIST } from '../../Modals/BaseProjectModal/types'
import * as Styled from './styled'
import ProjectListItem from './ProjectListItem'
import SortDropDown from './SortDropDown'
import { ProjectListProps } from './types'
import ProjectItemsMenu from './ProjectItemsMenu'
import EmptyProjectsListMessage from './EmptyProjectListMessage'

const ListContent: React.FunctionComponent<ProjectListProps> = ({
  copy,
  isLoading,
  projects,
  selectedItem,
  createProject,
  sortProjects,
  starProject,
  unStarProject,
  deleteProjects,
  moveProjects,
  editProject,
}) => {
  const [items, setSelectedItems] = useState([])

  useEffect(() => {
    const newItems = items.filter((item) =>
      projects.find((project) => project.id === item)
    )
    setSelectedItems(newItems)
  }, [projects])

  const arrangeItems = (id: string, ids: string[], selected: boolean): string[] => {
    const selectedItems = Object.assign([], ids)
    if (selected) {
      selectedItems.push(id)
      return selectedItems
    }

    return selectedItems.filter((existingId) => id !== existingId)
  }

  const clearAllSelected = () => {
    setSelectedItems([])
  }

  const onSelectItem = useCallback(
    (id: string, selected: boolean) => {
      setSelectedItems((prevItems) => {
        const newItems = arrangeItems(id, prevItems, selected)
        return newItems
      })
    },
    [items, setSelectedItems]
  )

  const onSelectStarred = (id: string, selected: boolean) => {
    if (selected) {
      starProject(id)
    } else {
      unStarProject(id)
    }
  }

  const selectAll = () => {
    const ids = projects.map((project) => project.id)
    setSelectedItems(ids)
  }

  const onEditHandler = useCallback(() => {
    if (items.length === 1) {
      const [id] = items
      const [project] = projects.filter((project) => id === project.id)
      editProject(project.name, project.defaultStyle, id)
    }
  }, [projects, items, editProject])

  return (
    <>
      <Styled.SortContainer>
        <SortDropDown sortProjects={sortProjects} />
      </Styled.SortContainer>
      {isLoading && <LoadingDots message="Loading your projects" />}
      {!isLoading && (
        <>
          <Styled.CreateProjectContainer>
            <Styled.CreateProjectButton
              onClick={createProject}
              data-test="create-project-button"
              role="button"
            >
              + {MODAL_TITLE_CREATE_CITATION_LIST}
            </Styled.CreateProjectButton>
          </Styled.CreateProjectContainer>
          {!projects.length && <EmptyProjectsListMessage copy={copy} />}
          {projects.map((project, index) => (
            <ProjectListItem
              key={project.id}
              {...project}
              starred={project.starred || selectedItem === ProjectTypeKeys.STARRED}
              index={index}
              style={project.defaultStyle}
              onMove={() => moveProjects([project.id])}
              onDelete={() => deleteProjects([project.id])}
              onEdit={editProject}
              selected={items.includes(project.id)}
              onSelect={onSelectItem}
              onSelectStarred={onSelectStarred}
            />
          ))}
          {!!items.length && (
            <ClientOnlyPortal selector="#checkbox-menu">
              <ProjectItemsMenu
                numItems={items.length}
                selectAll={selectAll}
                clearAllSelected={clearAllSelected}
                onMove={() => moveProjects(items)}
                onDelete={() => deleteProjects(items)}
                onEdit={onEditHandler}
              />
            </ClientOnlyPortal>
          )}
        </>
      )}
    </>
  )
}

export const ProjectsList: React.FunctionComponent<ProjectListProps> = (props) => {
  const { className } = props
  return (
    <Styled.ProjectListContainer className={className} data-test="project-list">
      <ListContent {...props} />
    </Styled.ProjectListContainer>
  )
}

const mapStateToProps = (state: AppState) => ({
  projects: projectsSelector(state),
  isLoading: areProjectsLoadingSelector(state),
  selectedItem: projectFilterSelector(state),
})

// TODO: update stubs
const mapDispatchToProps = {
  unStarProject,
  starProject,
  sortProjects,
  createProject: () => openModal({ name: CREATE_PROJECT_MODAL, data: {} }),
  deleteProjects: (ids) =>
    openModal({ name: DELETE_PROJECT_MODAL, data: { ids: [...ids] } }),
  editProject: (name, style, id) =>
    openModal({ name: EDIT_PROJECT_MODAL, data: { name, style, id } }),
  moveProjects: (ids) => openModal({ name: MOVE_PROJECT_MODAL, data: { ids: [...ids] } }),
}

export default connect(mapStateToProps, mapDispatchToProps)(ProjectsList)
